/**
 * Global tooltip handler. Picks up any element with `data-tooltip="..."`
 * anywhere on the page and:
 *
 *   1. Ensures it has a visible trigger (auto-inserts a small `i` button if
 *      the markup doesn't already include `.cwp-tooltip__trigger`).
 *   2. Renders the hint copy into a single shared popover positioned next
 *      to the trigger.
 *   3. Toggles the popover on hover / focus / tap, supports Escape to close.
 *
 * Markup contracts that work:
 *
 *   <span data-tooltip="…">term</span>                     // bare attr
 *   <span class="cwp-tooltip" data-tooltip="…">term</span> // styled span
 *   <abbr data-tooltip="…">RTP</abbr>                      // semantic abbr
 *
 * Anything else with `[data-tooltip]` (rating-breakdown rows, bonus
 * boxes, etc.) is also covered automatically.
 */

const POPOVER_ID = 'cwp-tooltip-popover';
const VISIBLE_CLASS = 'is-visible';

let popoverEl = null;
let activeTrigger = null;
let hideTimer = null;

export const cwpTooltip = {
	init() {
		if (typeof document === 'undefined') return;

		this._ensurePopover();
		this._enhanceAll();
		this._bindGlobalEvents();
	},

	_ensurePopover() {
		popoverEl = document.getElementById(POPOVER_ID);

		if (!popoverEl) {
			popoverEl = document.createElement('div');
			popoverEl.id = POPOVER_ID;
			popoverEl.className = 'cwp-tooltip-popover';
			popoverEl.setAttribute('role', 'tooltip');
			popoverEl.setAttribute('aria-hidden', 'true');
			document.body.appendChild(popoverEl);
		}
	},

	_enhanceAll() {
		document.querySelectorAll('[data-tooltip]').forEach((el) => {
			this._enhance(el);
		});

		// Watch for late additions (AJAX'd content, dynamic blocks).
		if (typeof MutationObserver !== 'undefined') {
			const mo = new MutationObserver((mutations) => {
				mutations.forEach((m) => {
					m.addedNodes.forEach((node) => {
						if (!(node instanceof HTMLElement)) return;
						if (node.hasAttribute('data-tooltip')) this._enhance(node);
						node
							.querySelectorAll?.('[data-tooltip]')
							.forEach((child) => this._enhance(child));
					});
				});
			});
			mo.observe(document.body, { childList: true, subtree: true });
		}
	},

	_enhance(host) {
		if (host.dataset.tooltipBound === '1') return;
		host.dataset.tooltipBound = '1';

		// If host has no obvious trigger, add a discreet `i` button so
		// keyboard + touch users can summon the tooltip.
		const hasTrigger = host.querySelector('.cwp-tooltip__trigger');
		const isInteractive =
			host.tagName === 'BUTTON' ||
			host.tagName === 'A' ||
			host.hasAttribute('tabindex');

		if (!hasTrigger && !isInteractive) {
			const btn = document.createElement('button');
			btn.type = 'button';
			btn.className = 'cwp-tooltip__trigger';
			btn.setAttribute(
				'aria-label',
				host.getAttribute('aria-label') || 'More info'
			);
			btn.tabIndex = 0;
			btn.innerHTML = '<span aria-hidden="true">i</span>';
			host.appendChild(btn);
		}

		const trigger = host.querySelector('.cwp-tooltip__trigger') || host;

		const open = () => this._show(host, trigger);
		const close = () => this._scheduleHide();

		trigger.addEventListener('mouseenter', open);
		trigger.addEventListener('focus', open);
		host.addEventListener('mouseleave', close);
		trigger.addEventListener('blur', close);

		trigger.addEventListener('click', (event) => {
			// Only intercept clicks on the `i` button — not on links wrapped
			// in `[data-tooltip]`.
			if (trigger.classList.contains('cwp-tooltip__trigger')) {
				event.preventDefault();
				if (activeTrigger === trigger) {
					this._hide();
				} else {
					open();
				}
			}
		});
	},

	_show(host, trigger) {
		if (!popoverEl) return;

		const text = host.getAttribute('data-tooltip') || '';
		if (!text) return;

		clearTimeout(hideTimer);

		popoverEl.textContent = text;
		popoverEl.dataset.variant = host.dataset.tooltipVariant || '';
		popoverEl.classList.add(VISIBLE_CLASS);
		popoverEl.setAttribute('aria-hidden', 'false');
		activeTrigger = trigger;

		this._position(trigger);
	},

	_scheduleHide() {
		clearTimeout(hideTimer);
		hideTimer = setTimeout(() => this._hide(), 120);
	},

	_hide() {
		if (!popoverEl) return;
		popoverEl.classList.remove(VISIBLE_CLASS);
		popoverEl.setAttribute('aria-hidden', 'true');
		activeTrigger = null;
	},

	_position(trigger) {
		if (!popoverEl || !trigger) return;

		const rect = trigger.getBoundingClientRect();
		const pop = popoverEl.getBoundingClientRect();
		const margin = 8;

		let top = rect.bottom + window.scrollY + margin;
		let left =
			rect.left + window.scrollX + rect.width / 2 - pop.width / 2;

		// Flip above if there's not enough room below.
		const viewportBottom = window.scrollY + window.innerHeight;
		if (top + pop.height > viewportBottom - margin) {
			top = rect.top + window.scrollY - pop.height - margin;
			popoverEl.dataset.placement = 'top';
		} else {
			popoverEl.dataset.placement = 'bottom';
		}

		// Clamp horizontally inside viewport.
		const minLeft = window.scrollX + margin;
		const maxLeft =
			window.scrollX + document.documentElement.clientWidth - pop.width - margin;
		left = Math.min(Math.max(left, minLeft), maxLeft);

		popoverEl.style.top = `${Math.round(top)}px`;
		popoverEl.style.left = `${Math.round(left)}px`;
	},

	_bindGlobalEvents() {
		document.addEventListener('keydown', (event) => {
			if (event.key === 'Escape') this._hide();
		});

		document.addEventListener('click', (event) => {
			if (!activeTrigger) return;
			const target = event.target;
			if (
				target instanceof Node &&
				!activeTrigger.contains(target) &&
				!popoverEl.contains(target)
			) {
				this._hide();
			}
		});

		window.addEventListener(
			'scroll',
			() => {
				if (activeTrigger) this._position(activeTrigger);
			},
			{ passive: true }
		);

		window.addEventListener('resize', () => {
			if (activeTrigger) this._position(activeTrigger);
		});
	},
};
